/**
 * Shared building blocks for the Atlas text artifacts (ROI Check, Competitors
 * Assessment, 5 Question Screen, Market Research). These artifacts are analytical
 * (signals, verdicts, tables, sources, actions) rather than the evidence-tagged
 * field cards used by JTBD/Persona — so they get their own presentational kit.
 *
 * Every Atlas renderer keeps the parsed JSON object as its model (lossless), so
 * `toJson` just returns it and edits are shallow narrative tweaks.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@ideoz/ui';
import { esc } from './html';

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
export const str = (v: unknown): string => (typeof v === 'string' ? v : '');
export const strArr = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];

// -- enum → label / tone -----------------------------------------------------

type Tone = 'emerald' | 'amber' | 'red' | 'sky' | 'gray';

const TONE_CLS: Record<Tone, string> = {
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  red: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  sky: 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

/** Human label for an enum-ish string, with a few known acronyms fixed up. */
export const humanizeEnum = (raw: string): string => {
  if (!raw) return '';
  const words = raw.replace(/[_-]+/g, ' ').trim().split(/\s+/);
  return words
    .map((w) => (w.toLowerCase() === 'roi' ? 'ROI' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
};

/** Map a signal/verdict value to a tone by keyword — positive→green, weak→red. */
export const toneFor = (value: string): Tone => {
  const v = (value || '').toLowerCase();
  if (
    /(high_roi|high|strong|ready|clear gap|clear_gap|straightforward|aligned|open window|right-time|right time|excitement|white_space|white space)/.test(
      v
    )
  )
    return 'emerald';
  if (
    /(moderate|medium|adequate|has_gaps|has gaps|complex|adjacent|performance|contested|early|maturing|growing)/.test(
      v
    )
  )
    return 'amber';
  if (
    /(low_roi|low|weak|unaddressed|major_red_flags|red flag|misaligned|too late|too-late|too early|too-early|threshold)/.test(
      v
    )
  )
    return 'red';
  if (/(unclear|unknown|undetermined|no signal|table_stakes|table stakes)/.test(v)) return 'gray';
  return 'sky';
};

// -- presentational ----------------------------------------------------------

export const Pill: React.FC<{ value: string; tone?: Tone; className?: string }> = ({
  value,
  tone,
  className,
}) => {
  if (!value) return null;
  const t = tone ?? toneFor(value);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium leading-none',
        TONE_CLS[t],
        className
      )}
    >
      {humanizeEnum(value)}
    </span>
  );
};

/** Bordered section with an uppercase title and optional trailing node. */
export const Section: React.FC<{
  title: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, trailing, children }) => (
  <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </h3>
      {trailing}
    </div>
    {children}
  </div>
);

/** Label / value row (value can be prose or any node). */
export const KV: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-1 gap-1 py-2.5 sm:grid-cols-[160px_1fr] sm:gap-4">
    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</span>
    <div className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">{children}</div>
  </div>
);

export const Prose: React.FC<{ children?: string | null }> = ({ children }) =>
  children ? (
    <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700 dark:text-gray-200">
      {children}
    </p>
  ) : null;

export const Divided: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="divide-y divide-gray-100 dark:divide-gray-800">{children}</div>
);

/** A simple table with a header row. Cells may be strings or nodes. */
export const Table: React.FC<{ head: string[]; rows: React.ReactNode[][] }> = ({ head, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="text-start text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {head.map((h, i) => (
            <th
              key={i}
              className="border-b border-gray-200 px-2 py-2 font-medium dark:border-gray-700"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className="align-top">
            {row.map((cell, ci) => (
              <td
                key={ci}
                className="border-b border-gray-100 px-2 py-2 text-gray-700 dark:border-gray-800 dark:text-gray-200"
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Renders a `sources` array — items may be plain strings or objects. */
const sourceLabel = (s: unknown): string => {
  if (typeof s === 'string') return s;
  if (isRecord(s)) {
    const label = str(s.label) || str(s.source) || str(s.name) || str(s.title);
    const type = str(s.type) || str(s.tag);
    return (
      [type ? `(${humanizeEnum(type)})` : '', label].filter(Boolean).join(' ').trim() ||
      JSON.stringify(s)
    );
  }
  return String(s ?? '');
};

export const SourcesBlock: React.FC<{ sources: unknown }> = ({ sources }) => {
  const { t } = useTranslation();
  const list = Array.isArray(sources) ? sources : [];
  if (list.length === 0) return null;
  return (
    <Section title={t('artifacts.atlas.sources')}>
      <ul className="space-y-1.5">
        {list.map((s, i) => (
          <li key={i} className="text-xs text-gray-500 dark:text-gray-400">
            {sourceLabel(s)}
          </li>
        ))}
      </ul>
    </Section>
  );
};

export const ActionsBlock: React.FC<{ actions: unknown }> = ({ actions }) => {
  const { t } = useTranslation();
  const list = strArr(actions);
  if (list.length === 0) return null;
  return (
    <Section title={t('artifacts.atlas.actions')}>
      <ul className="space-y-2">
        {list.map((a, i) => (
          <li key={i} className="flex gap-2 text-sm text-gray-700 dark:text-gray-200">
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-primary-50 text-[11px] font-semibold text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
              {i + 1}
            </span>
            <span>{a}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
};

// -- narrative editor --------------------------------------------------------

const inputCls =
  'w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';

/** One editable narrative field: reads/writes a string on a deep-cloned model. */
export interface EditField<M> {
  label: string;
  rows?: number;
  hint?: string;
  get: (m: M) => string;
  set: (m: M, v: string) => void; // mutates the clone in place
}

/**
 * Build an Editor that edits a handful of narrative fields (verdict reasoning,
 * next steps, actions…) while preserving the full analytical structure. Deep
 * copies the model, applies edits, and returns the JSON on save.
 */
export function makeNarrativeEditor<M extends Record<string, unknown>>(
  fields: EditField<M>[],
  toJson: (m: M) => Record<string, unknown>
): React.FC<{ model: M; onReady: (getJson: () => Record<string, unknown>) => void }> {
  const Editor: React.FC<{
    model: M;
    onReady: (getJson: () => Record<string, unknown>) => void;
  }> = ({ model, onReady }) => {
    const [draft, setDraft] = React.useState<M>(() => structuredClone(model));
    const ref = React.useRef(draft);
    ref.current = draft;

    React.useEffect(() => {
      onReady(() => toJson(ref.current));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = (f: EditField<M>, v: string) =>
      setDraft((prev) => {
        const next = structuredClone(prev);
        f.set(next, v);
        return next;
      });

    return (
      <div dir="ltr" className="mx-auto flex max-w-2xl flex-col gap-4 text-start">
        <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Editing the narrative fields — the analytical tables and signals are preserved as-is.
          </p>
          <div className="flex flex-col gap-4">
            {fields.map((f) => (
              <div key={f.label}>
                <label className="mb-1 block text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {f.label}
                  {f.hint && (
                    <span className="ms-2 text-[11px] font-normal text-gray-400">{f.hint}</span>
                  )}
                </label>
                <textarea
                  value={f.get(draft)}
                  rows={f.rows ?? 2}
                  onChange={(e) => update(f, e.target.value)}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  return Editor;
}

// -- html helpers (Atlas cards) ----------------------------------------------

export const sectionHtml = (title: string, inner: string): string =>
  `<section class="card"><h3>${esc(title)}</h3>${inner}</section>`;

export const proseHtml = (text: string): string => (text ? `<p>${esc(text)}</p>` : '');

export const listHtml = (items: string[]): string =>
  items.length ? `<ul>${items.map((i) => `<li>${esc(i)}</li>`).join('')}</ul>` : '';

export const sourcesHtml = (sources: unknown): string => {
  const list = Array.isArray(sources) ? sources.map(sourceLabel) : [];
  return list.length ? sectionHtml('Sources', listHtml(list)) : '';
};

export const actionsHtml = (actions: unknown): string => {
  const list = strArr(actions);
  return list.length ? sectionHtml('Actions', listHtml(list)) : '';
};
